import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';

interface DiagnosticEvent {
    id: number;
    toolCode: string;
    toolName: string;
    level: string;
    source: string;
    message: string;
    createdAt: string;
}

interface DiagnosticsSummary {
    totalEvents: number;
    byLevel: { level: string; count: number }[];
    topTools: { toolCode: string; toolName: string; count: number }[];
    recentEvents: DiagnosticEvent[];
}

interface ReportSummary {
    id: number;
    toolId: number;
    toolCode: string;
    toolName: string;
    title: string;
    type: string;
    status: string;
    createdAt: string;
}

const LEVEL_BADGE: Record<string, string> = {
    Error: 'bg-danger',
    Warning: 'bg-warning text-dark',
    Info: 'bg-info text-dark',
};

const QualityOverviewPage: React.FC = () => {
    const { showToast } = useToast();
    const [diagnostics, setDiagnostics] = useState<DiagnosticsSummary | null>(null);
    const [reports, setReports] = useState<ReportSummary[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [diagnosticData, openReports] = await Promise.all([
                apiService.adminGetGlobalDiagnostics<DiagnosticsSummary>({ sinceHours: 24, limit: 12 }),
                apiService.adminGetReportsCrossTool<ReportSummary>({ status: 'Open' }),
            ]);
            setDiagnostics(diagnosticData);
            setReports(openReports);
        } catch {
            showToast('Errore', 'Impossibile aggiornare il Quality Center', 'danger');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => { void load(); }, [load]);

    const counts = useMemo(() => {
        const result: Record<string, number> = { Error: 0, Warning: 0, Info: 0 };
        for (const item of diagnostics?.byLevel ?? []) result[item.level] = item.count;
        return result;
    }, [diagnostics]);

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h5 className="mb-1">Situazione nelle ultime 24 ore</h5>
                    <div className="small text-body-secondary">Dati reali raccolti da frontend, backend e utenti.</div>
                </div>
                <button className="btn btn-sm btn-outline-primary" onClick={load} disabled={loading}>
                    <i className="bi bi-arrow-clockwise me-1" />
                    {loading ? 'Aggiorno…' : 'Aggiorna'}
                </button>
            </div>

            <div className="row g-3 mb-3">
                <MetricCard label="Errori" value={counts.Error} icon="bi-x-octagon" color="danger" loading={loading}
                    to="/quality/issues?level=Error" />
                <MetricCard label="Warning" value={counts.Warning} icon="bi-exclamation-triangle" color="warning" loading={loading}
                    to="/quality/issues?level=Warning" />
                <MetricCard label="Segnalazioni aperte" value={reports.length} icon="bi-flag" color="primary" loading={loading}
                    to="/quality/reports?status=Open" />
                <MetricCard label="Eventi totali" value={diagnostics?.totalEvents ?? 0} icon="bi-activity" color="info" loading={loading}
                    to="/quality/events" />
            </div>

            <div className="row g-3 mb-3">
                <div className="col-12 col-xl-7">
                    <div className="card shadow-sm h-100">
                        <div className="card-header bg-body-tertiary d-flex justify-content-between align-items-center">
                            <strong>Segnali recenti</strong>
                            <Link to="/quality/events" className="btn btn-sm btn-link text-decoration-none">Vedi eventi</Link>
                        </div>
                        <div className="list-group list-group-flush">
                            {!loading && (diagnostics?.recentEvents.length ?? 0) === 0 && (
                                <div className="p-4 text-center text-body-secondary small">Nessun evento nelle ultime 24 ore.</div>
                            )}
                            {diagnostics?.recentEvents.slice(0, 6).map(event => (
                                <Link
                                    key={event.id}
                                    to={`/quality/events?toolCode=${encodeURIComponent(event.toolCode)}&level=${encodeURIComponent(event.level)}`}
                                    className="list-group-item list-group-item-action d-flex gap-3 align-items-start">
                                    <span className={`badge ${LEVEL_BADGE[event.level] ?? 'bg-secondary'} mt-1`}>{event.level}</span>
                                    <div className="min-w-0 flex-grow-1">
                                        <div className="small fw-semibold text-truncate">{event.message}</div>
                                        <div className="small text-body-secondary">
                                            {event.toolName} · {event.source} · {new Date(event.createdAt).toLocaleString('it-IT')}
                                        </div>
                                    </div>
                                    <i className="bi bi-chevron-right text-body-secondary" />
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="col-12 col-xl-5">
                    <div className="card shadow-sm h-100">
                        <div className="card-header bg-body-tertiary d-flex justify-content-between align-items-center">
                            <strong>Tool con più eventi</strong>
                            <span className="small text-body-secondary">24h</span>
                        </div>
                        <div className="card-body">
                            {!loading && (diagnostics?.topTools.length ?? 0) === 0 && (
                                <div className="text-body-secondary small">Nessun tool da evidenziare.</div>
                            )}
                            <div className="d-flex flex-column gap-3">
                                {diagnostics?.topTools.map((tool, index) => {
                                    const max = diagnostics.topTools[0]?.count || 1;
                                    const percentage = Math.max(6, Math.round((tool.count / max) * 100));
                                    return (
                                        <div key={tool.toolCode}>
                                            <div className="d-flex justify-content-between align-items-center small mb-1">
                                                <Link to={`/quality/issues?toolCode=${encodeURIComponent(tool.toolCode)}`}
                                                    className="text-decoration-none fw-semibold">
                                                    {index + 1}. {tool.toolName}
                                                </Link>
                                                <span>{tool.count}</span>
                                            </div>
                                            <div className="progress" style={{ height: 6 }}>
                                                <div className="progress-bar" style={{ width: `${percentage}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm">
                <div className="card-header bg-body-tertiary">
                    <strong>Pipeline di correzione</strong>
                </div>
                <div className="card-body">
                    <div className="quality-pipeline">
                        <PipelineStep icon="bi-broadcast" title="Raccolta" detail="Eventi e segnalazioni" state="active" />
                        <PipelineStep icon="bi-intersect" title="Correlazione" detail="Raggruppamento problemi" state="active" />
                        <PipelineStep icon="bi-robot" title="Analisi IA" detail="Proposta della soluzione" state="planned" />
                        <PipelineStep icon="bi-person-check" title="Approvazione" detail="Verifica amministratore" state="planned" />
                        <PipelineStep icon="bi-rocket-takeoff" title="Deploy" detail="Correzione e validazione" state="planned" />
                    </div>
                    <div className="alert alert-light border small mt-3 mb-0">
                        Raccolta e correlazione sono già utilizzabili. Analisi, approvazione e deploy verranno abilitati
                        quando saranno disponibili issue persistite, repository sandbox e audit delle modifiche.
                    </div>
                </div>
            </div>
        </>
    );
};

const MetricCard: React.FC<{
    label: string;
    value: number;
    icon: string;
    color: string;
    loading: boolean;
    to: string;
}> = ({ label, value, icon, color, loading, to }) => (
    <div className="col-12 col-sm-6 col-xl-3">
        <Link to={to} className="card shadow-sm h-100 text-decoration-none text-body quality-metric-card">
            <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                    <div className="small text-body-secondary">{label}</div>
                    <div className={`fs-3 fw-bold text-${color}`}>
                        {loading ? <span className="placeholder col-5" /> : value}
                    </div>
                </div>
                <i className={`bi ${icon} fs-1 text-${color} opacity-25`} />
            </div>
        </Link>
    </div>
);

const PipelineStep: React.FC<{
    icon: string;
    title: string;
    detail: string;
    state: 'active' | 'planned';
}> = ({ icon, title, detail, state }) => (
    <div className={`quality-pipeline-step ${state}`}>
        <div className="quality-pipeline-icon"><i className={`bi ${icon}`} /></div>
        <div>
            <div className="fw-semibold small">{title}</div>
            <div className="text-body-secondary quality-pipeline-detail">{detail}</div>
        </div>
        <span className={`badge ms-auto ${state === 'active' ? 'bg-success-subtle text-success-emphasis' : 'bg-light text-body-secondary border'}`}>
            {state === 'active' ? 'Attivo' : 'Da attivare'}
        </span>
    </div>
);

export default QualityOverviewPage;
